import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';

// Returns the value of `value` from the previous render. We use it to know
// where the user was navigating from when they tap "back", so we can decide
// whether `navigate(-1)` would actually return them to a sensible place
// (same section) or land them on an unrelated shell page (cross-section
// drawer hop) where we'd rather close the webview and let the native app
// show its own home.
function usePreviousPathname(pathname: string): string | null {
  const ref = useRef<string | null>(null);
  useEffect(() => {
    ref.current = pathname;
  }, [pathname]);
  return ref.current;
}

function topLevelSegment(pathname: string | null): string | null {
  if (!pathname) return null;
  const [first] = pathname.replace(/^\/|\/$/g, '').split('/');
  return first || null;
}

interface Props {
  forceClose?: boolean;
}
export const WebviewBackButton: React.FC<Props> = ({ forceClose = false }) => {
  const {
    isWebview,
    postMessage,
    closeable,
    supportsNativeDrawerToggle,
    toggleNativeDrawer,
  } = useNativeBridge();
  const navigate = useNavigate();
  const location = useLocation();

  // Routes that represent the top-level "shell" pages of the mobile app.
  // On these we either open the native drawer or close the webview; we never
  // try to go back in history.
  const baseRoutes = [
    '/clients/',
    '/embed/dashboard/',
    '/appointments/',
    '/messages/',
  ];

  const isBaseRoute = baseRoutes.includes(location.pathname);
  const hasNoHistory = location.key === 'default';

  const previousPathname = usePreviousPathname(location.pathname);
  // We crossed shells when the user got to the current page from a different
  // top-level section (e.g. landed on /messages/, opened the drawer, picked
  // Products and ended up on /products or /purchase/products). In that case
  // navigate(-1) would just take them back to /messages/, which is not what
  // "back" should mean from a drawer destination. Instead we close the
  // webview so the native shell shows its own dashboard.
  const previousSection = topLevelSegment(previousPathname);
  const currentSection = topLevelSegment(location.pathname);
  const crossedShells =
    !!previousSection &&
    !!currentSection &&
    previousSection !== currentSection;

  // Hamburger only shows on top-level shell pages with native drawer support.
  // On any sub-page (e.g. /products) we always show the back chevron, even if
  // iOS loaded it fresh (which would otherwise reset history.key to 'default').
  const useHamburger = supportsNativeDrawerToggle() && (isBaseRoute || hasNoHistory);

  const handleBackClick = () => {
    if (useHamburger) {
      toggleNativeDrawer();
      return;
    }
    if (isBaseRoute || forceClose) {
      postMessage('close'); // Close the window
      return;
    }
    if (hasNoHistory || crossedShells) {
      // Either we have no history to pop, or popping it would land the user
      // on an unrelated shell page they don't think of as "the previous
      // page" (drawer hop between sections). Closing the webview lets the
      // native app surface its own home/dashboard, which is the behaviour
      // users expect from the back button on these top-level destinations.
      postMessage('close');
      return;
    }
    navigate(-1); // Go back in history
  };

  const backIcon = useHamburger ? (
    <Icon icon={faBars} />
  ) : (
    <Icon icon={faChevronLeft} />
  );

  if (!isWebview) return null;
  if (isBaseRoute && !closeable && !supportsNativeDrawerToggle) return null;

  return (
    <IconButton onClick={handleBackClick} sx={{ mr: 0 }}>
      {backIcon}
    </IconButton>
  );
};
